// Comprehensive Help Center Content for Constant Love

export const helpContent = {
  'start-here': {
    title: '🌟 Welcome to Constant Love!',
    sections: [
      {
        heading: 'Getting Started Guide',
        items: [
          {
            q: 'What is Constant Love?',
            a: 'Constant Love is your AI-powered self-love and confidence-building companion. We help you rewire negative thought patterns through daily affirmations, journaling, an AI companion (Conchi), astrology guidance, and gamified activities. Our mission is to help you fall in love with yourself.'
          },
          {
            q: 'How do I create my account?',
            a: '1. Click "Get Started" or "Sign Up" on the landing page\n2. Enter your name, email, and create a password\n3. Add your personal details (gender, birth date for astrology features)\n4. Complete the onboarding flow with personality assessment\n5. Start your 7-day free trial of the Growth plan!'
          },
          {
            q: 'What happens during onboarding?',
            a: 'Our onboarding helps personalize your experience:\n• Learn about the NeuroLove Method through educational slides\n• Take a personality assessment (10 questions)\n• Discover your self-love archetype\n• Get a tour of all features\n• Learn how to install the app on your home screen\n\nYou can skip any step and revisit it later in your profile.'
          },
          {
            q: 'How do I install the app on my home screen?',
            a: 'iOS (Safari): Tap Share button → "Add to Home Screen" → Tap "Add"\n\nAndroid (Chrome): Tap menu (⋮) → "Add to Home screen" → Tap "Install"\n\nDesktop: Look for install icon in address bar or File menu\n\nOnce installed, launch Constant Love like any other app!'
          },
        ]
      },
      {
        heading: 'Understanding Your Free Trial',
        items: [
          {
            q: 'What is included in my 7-day free trial?',
            a: 'All new users get a 7-day free trial of our Growth (paid) plan with:\n• Unlimited conversations with Conchi\n• Unlimited AI journal insights\n• Unlimited Divine Guidance readings\n• Unlimited Vision Boards\n• Unlimited Love Vault saves\n• Full Love Garden and Community access\n• NO credit card required!'
          },
          {
            q: 'What happens after my trial ends?',
            a: 'After 7 days, you\'ll automatically switch to our Reflect (free) plan with limited features:\n• 1 daily affirmation\n• 1 AI journal insight per week\n• 5 Conchi messages per day\n• 1 Divine Guidance reading per week\n• 1 Vision Board maximum\n• 10 Love Vault saves per month\n• Full Love Garden & Community access\n\nYou can upgrade to Growth ($17/month) anytime for unlimited access.'
          }
        ]
      },
      {
        heading: 'Your First Week',
        items: [
          {
            q: 'What should I do first?',
            a: 'Day 1: Get your personalized daily affirmation and save it\nDay 2: Try the Love Garden game and start building your Love Score\nDay 3: Have your first conversation with Conchi about your self-love journey\nDay 4: Create your first journal entry with AI insights\nDay 5: Get a Divine Guidance reading based on your birth chart\nDay 6: Build your first Vision Board with goals and affirmations\nDay 7: Explore the Community and connect with others'
          }
        ]
      }
    ]
  },
  'affirmations': {
    title: '💕 Personalized Daily Affirmations',
    sections: [
      {
        heading: 'How It Works',
        items: [
          {
            q: 'What are daily affirmations?',
            a: 'Every day, you receive a personalized affirmation based on your current mood and emotional state. These positive statements help rewire negative thought patterns and build self-love.'
          },
          {
            q: 'How do I get my daily affirmation?',
            a: '1. Check in with your mood on the dashboard\n2. Your affirmation generates based on how you feel\n3. Read it, save it to your Love Vault, or share it\n4. Come back daily for new affirmations!\n\nFree plan: 1 affirmation per day\nGrowth plan: Unlimited affirmations'
          },
          {
            q: 'Can I customize my affirmations?',
            a: 'Affirmations are personalized based on your mood check-ins, journal entries, and conversation history with Conchi. The more you use the app, the more personalized they become!'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'When should I read my affirmation?',
            a: 'Best times: Morning (sets positive tone for the day), Before bed (peaceful mindset), During stress (quick mental reset), Before important events (confidence boost)'
          },
          {
            q: 'How do I make affirmations more effective?',
            a: '• Say them out loud with conviction\n• Look in the mirror while saying them\n• Write them in your journal\n• Set them as phone wallpaper\n• Repeat throughout the day\n• Believe in what you\'re saying'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'My affirmation doesn\'t feel relevant',
            a: 'Try updating your mood check-in to be more specific. Affirmations adapt to your emotional state, so honest check-ins lead to better affirmations. Also, journal more frequently so the AI understands you better.'
          },
          {
            q: 'I\'ve reached my daily limit (free plan)',
            a: 'Free plan users get 1 personalized affirmation per day. You can still view saved affirmations in your Love Vault anytime. Upgrade to Growth plan for unlimited daily affirmations!'
          }
        ]
      }
    ]
  },
  'journaling': {
    title: '📝 AI-Powered Journaling',
    sections: [
      {
        heading: 'How It Works',
        items: [
          {
            q: 'What is AI-powered journaling?',
            a: 'Write your thoughts and feelings in your private journal, then get AI-generated insights that help you understand patterns, identify emotions, and gain clarity on your self-love journey.'
          },
          {
            q: 'How do I create a journal entry?',
            a: '1. Go to the Journal tab\n2. Click "New Entry"\n3. Write freely about your day, feelings, or thoughts\n4. Click "Get AI Insight" to receive personalized analysis\n5. Save your entry\n\nFree plan: 1 AI insight per week\nGrowth plan: Unlimited AI insights'
          },
          {
            q: 'What kind of insights will I get?',
            a: 'AI insights include:\n• Emotional pattern recognition\n• Positive reframing suggestions\n• Actionable self-care recommendations\n• Progress acknowledgments\n• Questions to deepen self-reflection'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'When should I journal?',
            a: 'Morning journaling: Set intentions for the day\nEvening journaling: Process the day\'s events\nTriggered journaling: When emotions run high\nWeekly reflection: Review patterns and growth\n\nConsistency matters more than timing!'
          },
          {
            q: 'What should I write about?',
            a: 'Write about anything! Ideas:\n• How you\'re feeling right now\n• Challenges you\'re facing\n• Wins and accomplishments\n• Relationships and interactions\n• Self-love progress\n• Gratitude and appreciation\n• Dreams and aspirations'
          },
          {
            q: 'How long should my entries be?',
            a: 'Quality over quantity! Write as little as a few sentences or as much as several paragraphs. The AI works well with entries of any length, but more detail gives better insights.'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'The AI insight isn\'t helpful',
            a: 'Try being more specific in your entry. Instead of "Bad day," write about what made it bad. The AI provides better insights with more context and emotional detail.'
          },
          {
            q: 'I\'ve used my weekly AI insight (free plan)',
            a: 'Free plan users get 1 AI insight per week. You can still create unlimited journal entries without insights. Insights reset every week. Upgrade to Growth for unlimited insights!'
          },
          {
            q: 'Is my journal private?',
            a: 'Yes! Your journal is completely private. Only you can see your entries. We use the content to personalize your experience (affirmations, Conchi responses), but no humans ever read your journal.'
          }
        ]
      }
    ]
  },
  'conchi': {
    title: '💬 Conchi - Your AI Self-Love Companion',
    sections: [
      {
        heading: 'Getting to Know Conchi',
        items: [
          {
            q: 'Who is Conchi?',
            a: 'Conchi is your 24/7 AI companion designed specifically for self-love support and emotional guidance. She remembers your conversations, understands your journey, and provides personalized encouragement based on your mood, journal entries, and personal goals.'
          },
          {
            q: 'How is Conchi different from ChatGPT?',
            a: 'Conchi is trained specifically for self-love and personal growth support. She:\n• Remembers your entire conversation history\n• Knows your mood patterns and journal entries\n• Provides proactive check-ins\n• Offers personalized affirmations\n• Focuses solely on your self-love journey\n\nChatGPT is a general assistant without this specialized knowledge.'
          },
          {
            q: 'Is Conchi a real therapist?',
            a: 'No. Conchi is an AI companion for emotional support and self-love guidance. She is NOT a licensed therapist, medical professional, or crisis counselor. For serious mental health concerns, please seek professional help.'
          }
        ]
      },
      {
        heading: 'Using Conchi',
        items: [
          {
            q: 'How do I talk to Conchi?',
            a: '1. Go to the Companion tab\n2. Type your message in the chat box\n3. Press send or hit Enter\n4. Conchi will respond based on your history and needs\n\nFree plan: 5 messages per day\nGrowth plan: Unlimited conversations'
          },
          {
            q: 'What can I talk to Conchi about?',
            a: 'You can discuss:\n• Your feelings and emotions\n• Self-love struggles\n• Relationships and boundaries\n• Personal growth goals\n• Daily challenges\n• Wins and accomplishments\n• Affirmations and mindset work\n• Anything on your mind!'
          },
          {
            q: 'Does Conchi remember previous conversations?',
            a: 'Yes! Conchi remembers everything you\'ve shared. This helps her provide more personalized, relevant support over time. You can view and delete conversation history in Profile Settings > Privacy.'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'How do I get the most out of Conchi?',
            a: '• Be honest and open about your feelings\n• Provide context about situations\n• Ask follow-up questions\n• Use Conchi for daily check-ins\n• Share your wins and struggles\n• Trust the process - it gets better with time!'
          },
          {
            q: 'What if Conchi says something unhelpful?',
            a: 'Let Conchi know! Say "That wasn\'t helpful" or "Can you rephrase that?" She\'ll adjust her response. The AI learns what works for you over time.'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'Conchi isn\'t responding',
            a: '1. Check your internet connection\n2. Refresh the page\n3. Try logging out and back in\n4. Clear browser cache\n5. If issue persists, contact support'
          },
          {
            q: 'I\'ve reached my daily message limit (free plan)',
            a: 'Free plan users get 5 messages with Conchi per day. Limits reset at midnight. Upgrade to Growth plan for unlimited conversations!'
          },
          {
            q: 'Conchi\'s response seems generic',
            a: 'Conchi provides better responses when you:\n• Write longer, more detailed messages\n• Share specific emotions and situations\n• Update your mood check-ins regularly\n• Journal frequently so she understands you better'
          }
        ]
      }
    ]
  },
  'love-garden': {
    title: '🎮 Love Garden - Match-3 Game',
    sections: [
      {
        heading: 'How to Play',
        items: [
          {
            q: 'What is Love Garden?',
            a: 'Love Garden is a relaxing match-3 puzzle game where you match flowers to grow your self-love garden. Playing earns you Love Score points and helps reinforce positive mindset patterns through gameplay!'
          },
          {
            q: 'How do I play?',
            a: '1. Go to the Love Garden tab\n2. Select a level (50 levels available)\n3. Match 3 or more flowers of the same color\n4. Complete level objectives (collect specific flowers, reach score, etc.)\n5. Earn Love Score points based on performance\n6. Unlock new levels as you progress!'
          },
          {
            q: 'How does scoring work?',
            a: 'You earn Love Score points for:\n• Completing levels\n• Making combo matches (4+  flowers)\n• Collecting Love Shards during gameplay\n• Finishing with time remaining\n• Achieving 3-star ratings\n\nHigher scores = more Love Score points!'
          }
        ]
      },
      {
        heading: 'Game Features',
        items: [
          {
            q: 'What are Love Shards?',
            a: 'Love Shards are special collectibles that appear during gameplay. Collect them to:\n• Boost your Love Score\n• Unlock special power-ups\n• Earn bonus points\n• Complete daily challenges'
          },
          {
            q: 'Are there power-ups?',
            a: 'Yes! Create special flowers by matching 4+ in specific patterns:\n• Row of 4: Line Blast (clears entire row or column)\n• L-shape: Bomb (explodes nearby flowers)\n• Row of 5: Rainbow flower (removes all of one color)\n\nUse power-ups strategically to complete challenging levels!'
          },
          {
            q: 'How many levels are there?',
            a: 'Love Garden features 50 beautifully designed levels with increasing difficulty. Each level has unique objectives and challenges. More levels are added regularly!'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'Tips for higher scores',
            a: '• Plan ahead - look for combo opportunities\n• Save power-ups for difficult sections\n• Prioritize level objectives first\n• Make matches from the bottom up\n• Collect all Love Shards before finishing\n• Take your time - no penalties for thinking!'
          },
          {
            q: 'How do I unlock new levels?',
            a: 'Complete the previous level to unlock the next one. You don\'t need 3 stars to progress - just finish the level! You can always replay levels to improve your score.'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'Game is laggy or slow',
            a: '1. Close other browser tabs\n2. Refresh the page\n3. Clear browser cache\n4. Try a different browser\n5. Check your internet connection\n6. Restart your device if issue persists'
          },
          {
            q: 'Level seems impossible',
            a: 'Try these strategies:\n• Focus on creating power-ups\n• Study the board before making moves\n• Work on level objectives, not just high score\n• Take breaks and come back fresh\n• Watch for hidden combo opportunities\n\nEvery level is solvable without spending anything!'
          },
          {
            q: 'My progress didn\'t save',
            a: 'Make sure you\'re logged in to your account. Progress saves automatically after completing each level. If you logged out or cleared cookies, you may lose unsaved progress.'
          }
        ]
      }
    ]
  },
  'divine-guidance': {
    title: '✨ Divine Guidance - Astrology Insights',
    sections: [
      {
        heading: 'What is Divine Guidance?',
        items: [
          {
            q: 'What is Divine Guidance?',
            a: 'Divine Guidance provides personalized astrology insights based on your birth chart. Get daily readings, understand your cosmic influences, and align your self-love journey with celestial wisdom.'
          },
          {
            q: 'How do I get my reading?',
            a: '1. Go to the Divine Guidance tab\n2. Ensure your birth date is in your profile\n3. Select focus area (love, career, personal growth, etc.)\n4. Receive your personalized reading\n\nFree plan: 1 reading per week\nGrowth plan: Unlimited readings'
          },
          {
            q: 'Do I need to know astrology?',
            a: 'Not at all! Divine Guidance is written in plain language. We translate complex astrological concepts into practical, actionable insights for your self-love journey.'
          }
        ]
      },
      {
        heading: 'Reading Types',
        items: [
          {
            q: 'What focus areas can I choose?',
            a: 'Available focus areas:\n• Self-Love & Confidence\n• Relationships & Boundaries\n• Career & Purpose\n• Personal Growth\n• Emotional Healing\n• Life Purpose\n• Current Challenges\n\nEach generates unique guidance based on current planetary transits and your birth chart.'
          },
          {
            q: 'How are readings personalized?',
            a: 'Readings consider:\n• Your sun, moon, and rising signs\n• Current planetary transits\n• Your chosen focus area\n• Seasonal energies\n• Your self-love journey stage\n\nEvery reading is unique to you and this moment!'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'When should I get a reading?',
            a: 'Best times:\n• Morning: Set intentions for the day\n• New/Full Moon: Major energy shifts\n• Making big decisions: Gain cosmic perspective\n• Feeling stuck: Get guidance on next steps\n• Weekly check-in: Understand upcoming energies'
          },
          {
            q: 'How do I apply Divine Guidance?',
            a: '• Read slowly and reflect on each point\n• Journal about insights that resonate\n• Save important readings to Love Vault\n• Discuss with Conchi for deeper understanding\n• Take action on practical suggestions\n• Trust your intuition above all'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'Reading doesn\'t resonate with me',
            a: 'Astrology is interpretive and not all readings will feel relevant. Try:\n• Choosing a different focus area\n• Checking again in a few days\n• Journaling about which parts DO resonate\n• Remembering readings are guidance, not rules'
          },
          {
            q: 'I\'ve used my weekly reading (free plan)',
            a: 'Free plan users get 1 Divine Guidance reading per week. Readings reset every 7 days. You can save your favorite readings to Love Vault to reference anytime. Upgrade to Growth for unlimited readings!'
          },
          {
            q: 'I don\'t know my exact birth time',
            a: 'That\'s okay! Readings work with just your birth date. Knowing your birth time provides more precision (especially for moon and rising signs), but isn\'t required for valuable insights.'
          }
        ]
      }
    ]
  },
  'vision-board': {
    title: '🎨 Vision Board - Manifest Your Goals',
    sections: [
      {
        heading: 'Creating Vision Boards',
        items: [
          {
            q: 'What is a Vision Board?',
            a: 'A Vision Board is a visual collage of your goals, dreams, and affirmations. Create digital boards to clarify desires, stay motivated, and manifest the life you want. It\'s your self-love vision made visible!'
          },
          {
            q: 'How do I create a Vision Board?',
            a: '1. Go to Vision Board tab\n2. Click "Create New Board"\n3. Choose a category (self-love, career, relationships, health, etc.)\n4. Add elements:\n   • Upload images\n   • Add text and affirmations\n   • Choose background colors/images\n   • Arrange elements how you like\n5. Save your board\n\nFree plan: 1 board maximum\nGrowth plan: Unlimited boards'
          },
          {
            q: 'What should I include on my board?',
            a: 'Include:\n• Visual representations of goals\n• Affirmations and mantras\n• Inspirational quotes\n• Photos of desired outcomes\n• Colors that inspire you\n• Words that motivate you\n• Images of your ideal self\n\nMake it personal and inspiring to YOU!'
          }
        ]
      },
      {
        heading: 'Board Categories',
        items: [
          {
            q: 'What categories can I create?',
            a: 'Popular categories:\n• Self-Love Journey\n• Career & Success\n• Relationships\n• Health & Wellness\n• Financial Goals\n• Personal Growth\n• Dream Life\n• Gratitude\n\nCreate custom categories for any area of life!'
          },
          {
            q: 'Can I have multiple boards?',
            a: 'Free plan: 1 board (you can edit it anytime)\nGrowth plan: Unlimited boards\n\nCreate separate boards for different life areas or update your board as goals evolve!'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'How do I make my Vision Board effective?',
            a: '• Be specific with your goals\n• Use present tense ("I am" not "I will")\n• Include images that evoke emotion\n• Keep it visible - view daily\n• Update as goals change\n• Feel the feelings of achievement\n• Take inspired action toward goals'
          },
          {
            q: 'How often should I view my board?',
            a: 'View your board:\n• Every morning for motivation\n• Before bed for subconscious programming\n• When feeling unmotivated\n• Monthly to track progress\n• Quarterly to update goals\n\nConsistent viewing reinforces your intentions!'
          },
          {
            q: 'When should I update my board?',
            a: 'Update when:\n• Goals are achieved (celebrate!)\n• Desires change\n• New dreams emerge\n• Quarterly life reviews\n• New Year or birthdays\n• Major life transitions\n\nVision Boards are living documents!'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'I can\'t upload images',
            a: 'Check:\n• File size (must be under 5MB)\n• File format (JPG, PNG, GIF supported)\n• Internet connection\n• Browser permissions for file uploads\n\nTry compressing large images before uploading.'
          },
          {
            q: 'I\'ve reached my board limit (free plan)',
            a: 'Free plan users can create 1 Vision Board. You can edit your existing board anytime or delete it to create a new one. Upgrade to Growth plan for unlimited Vision Boards!'
          },
          {
            q: 'How do I delete a board?',
            a: 'Go to Vision Board tab → Select board → Click settings/options menu → Delete board. Note: This action cannot be undone, so make sure you want to permanently remove it!'
          }
        ]
      }
    ]
  },
  'love-vault': {
    title: '💎 Love Vault - Save Your Favorites',
    sections: [
      {
        heading: 'What is Love Vault?',
        items: [
          {
            q: 'What is the Love Vault?',
            a: 'Love Vault is your personal collection of saved affirmations, Divine Guidance readings, and inspirational content. Save your favorites to revisit anytime for quick motivation and encouragement!'
          },
          {
            q: 'How do I save to Love Vault?',
            a: '1. Find content you love (affirmation, reading, etc.)\n2. Click the "Save" or heart icon\n3. Content is added to your Love Vault\n4. Access anytime from the Love Vault tab\n\nFree plan: 10 saves per month\nGrowth plan: Unlimited saves'
          },
          {
            q: 'What can I save?',
            a: 'You can save:\n• Daily affirmations\n• Divine Guidance readings\n• Inspiring community posts\n• Journal prompts\n• Conchi\'s best advice\n• Vision Board elements\n• Motivational quotes'
          }
        ]
      },
      {
        heading: 'Organizing Your Vault',
        items: [
          {
            q: 'Can I organize my saved items?',
            a: 'Yes! You can:\n• View all saved items chronologically\n• Filter by type (affirmations, readings, etc.)\n• Search by keyword\n• Delete items you no longer need\n• Export your vault as PDF or text'
          },
          {
            q: 'How do I remove items?',
            a: 'Go to Love Vault → Find the item → Click remove/unsave icon. This frees up space in your monthly limit if you\'re on the free plan.'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'How should I use my Love Vault?',
            a: 'Use your Love Vault as:\n• Morning motivation library\n• Emergency pick-me-up collection\n• Proof of your growth journey\n• Source of personalized affirmations\n• Reminder of guidance received\n• Inspiration for journal entries\n• Content for your Vision Board'
          },
          {
            q: 'What should I save?',
            a: 'Save content that:\n• Deeply resonates with you\n• Addresses recurring challenges\n• Inspires immediate positive emotion\n• Helps during specific situations\n• Marks important growth moments\n• You want to revisit regularly\n\nQuality over quantity!'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'I\'ve reached my monthly save limit (free plan)',
            a: 'Free plan users can save 10 items per month. Limits reset on the 1st of each month. You can:\n• Remove old saves to make room\n• Screenshot important content\n• Upgrade to Growth for unlimited saves\n• Prioritize your most meaningful items'
          },
          {
            q: 'Can I export my Love Vault?',
            a: 'Yes! Go to Profile Settings > Privacy > Export Data to download your entire Love Vault. You\'ll receive a file with all saved content for your records.'
          },
          {
            q: 'What happens to my saves if I downgrade?',
            a: 'Your saved items remain accessible. However, you won\'t be able to add new saves beyond the free plan limit (10/month) until your monthly reset or you upgrade again.'
          }
        ]
      }
    ]
  },
  'community': {
    title: '👥 Community - Connect & Share',
    sections: [
      {
        heading: 'About the Community',
        items: [
          {
            q: 'What is the Community?',
            a: 'The Constant Love Community is a safe, supportive space where members share their self-love journeys, celebrate wins, support each other, and find inspiration. It\'s fully accessible to both free and paid plan users!'
          },
          {
            q: 'How do I access the Community?',
            a: 'Go to the Community tab in your navigation menu. You\'ll see channels for different topics. Browse posts, like, comment, and share your own experiences!'
          },
          {
            q: 'Is the Community safe and moderated?',
            a: 'Yes! We have:\n• Community Guidelines everyone must follow\n• Active moderation team\n• Report feature for inappropriate content\n• Zero tolerance for harassment or hate\n• Focus on positivity and support\n\nWe work hard to keep the space safe and welcoming.'
          }
        ]
      },
      {
        heading: 'Participating in Community',
        items: [
          {
            q: 'How do I post?',
            a: '1. Go to Community tab\n2. Select a channel (Daily Wins, Challenges, Questions, etc.)\n3. Click "New Post"\n4. Write your message (text, images optional)\n5. Post to share with the community\n\nBe authentic and supportive!'
          },
          {
            q: 'What channels are available?',
            a: 'Community channels:\n• Daily Wins: Celebrate achievements\n• Challenges & Support: Share struggles, get help\n• Questions: Ask anything self-love related\n• Inspiration: Share quotes, affirmations, wisdom\n• Introductions: Welcome new members\n• Events: Community activities and challenges'
          },
          {
            q: 'Can I comment and like posts?',
            a: 'Yes! Engage by:\n• Liking posts that resonate\n• Commenting with support and encouragement\n• Sharing your own experiences\n• Asking thoughtful questions\n• Offering genuine advice\n\nAuthentic engagement builds stronger connections!'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'Community etiquette',
            a: '✅ DO:\n• Be kind and supportive\n• Celebrate others\' wins\n• Share authentically\n• Ask for help when needed\n• Respect different perspectives\n• Use content warnings for sensitive topics\n\n❌ DON\'T:\n• Judge or criticize\n• Give medical/legal advice\n• Share others\' private info\n• Promote products/services\n• Engage in arguments\n• Post triggering content without warning'
          },
          {
            q: 'How to get the most from Community',
            a: '• Post regularly, even small wins\n• Comment on others\' posts\n• Ask genuine questions\n• Share your growth journey\n• Offer support to others\n• Participate in community challenges\n• Be vulnerable yet boundaried'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'How do I report inappropriate content?',
            a: 'Click the "..." menu on any post or comment and select "Report." Choose the reason and provide details. Our moderation team reviews all reports within 24 hours.'
          },
          {
            q: 'Can I delete my posts or comments?',
            a: 'Yes! Go to your post/comment → Click "..." menu → Select "Delete." Note: Others may have already read or replied to your content before deletion.'
          },
          {
            q: 'Someone is being mean to me',
            a: 'We\'re sorry! Please:\n1. Report the content immediately\n2. Block the user if needed\n3. Email support@constantloveco.com with details\n\nWe take harassment seriously and act quickly.'
          }
        ]
      }
    ]
  },
  'love-score': {
    title: '🏆 Love Score - Track Your Progress',
    sections: [
      {
        heading: 'Understanding Love Score',
        items: [
          {
            q: 'What is Love Score?',
            a: 'Love Score is your gamified self-love progress tracker. Earn points through daily activities, unlock levels, and watch your self-love practice grow! It\'s a fun way to stay motivated and see tangible progress.'
          },
          {
            q: 'How do I earn Love Score points?',
            a: 'Earn points by:\n• Playing Love Garden game\n• Daily mood check-ins\n• Creating journal entries\n• Talking with Conchi\n• Getting Divine Guidance\n• Building Vision Boards\n• Completing daily challenges\n• Community engagement\n• Consistent streaks'
          },
          {
            q: 'What are Love Score levels?',
            a: 'As you earn points, you level up! Each level represents your deepening self-love practice:\n• Level 1-10: Self-Love Beginner\n• Level 11-25: Self-Love Apprentice\n• Level 26-50: Self-Love Practitioner\n• Level 51-100: Self-Love Master\n• Level 100+: Self-Love Guru\n\nHigher levels unlock special badges and perks!'
          }
        ]
      },
      {
        heading: 'Points System',
        items: [
          {
            q: 'How many points do different activities give?',
            a: 'Typical point values:\n• Love Garden game: 20-50 points (based on score)\n• Daily check-in: 5 points\n• Journal entry: 10 points\n• Conchi conversation: 5 points\n• Divine Guidance: 10 points\n• Vision Board creation: 15 points\n• Community post: 5 points\n• 7-day streak: 50 bonus points\n• 30-day streak: 200 bonus points'
          },
          {
            q: 'Are there bonus points?',
            a: 'Yes! Earn bonus points through:\n• Consecutive day streaks\n• Completing daily challenges\n• First-time activities\n• Special events\n• Milestone achievements\n• Perfect weeks (all activities completed)\n\nBonus multipliers apply during special events!'
          }
        ]
      },
      {
        heading: 'Badges & Achievements',
        items: [
          {
            q: 'What badges can I earn?',
            a: 'Earn badges for:\n• Reaching level milestones\n• Maintaining streaks\n• Completing all features\n• Community contributions\n• Game achievements\n• Consistency awards\n• Special events\n\nBadges display on your profile!'
          },
          {
            q: 'What do I get for leveling up?',
            a: 'Level up rewards:\n• New badges and titles\n• Special affirmations\n• Exclusive community features\n• Enhanced Love Garden levels\n• Profile customization options\n• Surprise rewards at major milestones\n\nThe biggest reward? Seeing your growth!'
          }
        ]
      },
      {
        heading: 'Best Practices',
        items: [
          {
            q: 'How do I maximize my Love Score?',
            a: '• Do something every day (even small)\n• Complete daily challenges\n• Maintain streaks\n• Diversify activities (don\'t just play games)\n• Engage with community\n• Set consistent routine\n• Focus on quality engagement\n• Enjoy the journey, not just the points!'
          },
          {
            q: 'Should I care about my Love Score?',
            a: 'Love Score is motivational, not mandatory! Use it as:\n• Visual proof of progress\n• Daily motivation tool\n• Fun gamification element\n• Streak accountability\n\nBUT remember: Your self-love journey is valid regardless of points. Some days you need rest, not points!'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'My points aren\'t updating',
            a: '1. Refresh the page\n2. Check your internet connection\n3. Wait a few minutes (sometimes there\'s delay)\n4. Log out and back in\n5. Contact support if issue persists\n\nPoints are awarded after completing activities, not during.'
          },
          {
            q: 'I lost my streak!',
            a: 'Streaks reset if you miss a day. Unfortunately, we can\'t manually restore streaks. But don\'t let this discourage you - start fresh today! Every streak started with day 1. You\'ve got this! 💪'
          },
          {
            q: 'Can I see my Love Score history?',
            a: 'Yes! Go to Profile → Love Score → View History to see:\n• Points earned by day\n• Activities completed\n• Level progression\n• Badge timeline\n• Streak calendar\n\nGreat for tracking patterns!'
          }
        ]
      }
    ]
  },
  'subscription': {
    title: '💳 Plans & Billing',
    sections: [
      {
        heading: 'Understanding Plans',
        items: [
          {
            q: 'What plans are available?',
            a: 'Reflect (Free) Plan:\n• 1 daily affirmation\n• 5 Conchi messages/day\n• 1 AI journal insight/week\n• 1 Divine Guidance/week\n• 10 Love Vault saves/month\n• 1 Vision Board max\n• Full Love Garden & Community\n\nGrowth Plan ($17/month):\n• UNLIMITED everything\n• All features unlocked\n• Priority support\n• Exclusive content'
          },
          {
            q: 'Do I get a free trial?',
            a: 'Yes! All new users get a 7-day free trial of the Growth plan with unlimited access to everything. No credit card required! After 7 days, you\'ll move to the Reflect (free) plan unless you subscribe.'
          },
          {
            q: 'How much does Growth plan cost?',
            a: 'Growth Plan: $17/month, billed monthly\n\nCancel anytime with no penalties or fees. You keep access until the end of your billing period.'
          }
        ]
      },
      {
        heading: 'Billing & Payments',
        items: [
          {
            q: 'How do I upgrade to Growth plan?',
            a: '1. Go to Profile → Subscription\n2. Click "Upgrade to Growth"\n3. Enter payment details (Stripe secure checkout)\n4. Confirm subscription\n5. Instant unlimited access!\n\nPayments are processed securely by Stripe.'
          },
          {
            q: 'What payment methods are accepted?',
            a: 'We accept:\n• Credit cards (Visa, Mastercard, Amex, Discover)\n• Debit cards\n• Apple Pay\n• Google Pay\n\nAll payments processed securely through Stripe. We never see your full card details.'
          },
          {
            q: 'When will I be charged?',
            a: 'You\'re charged:\n• Immediately when you upgrade\n• Then monthly on the same date\n• Automatic renewal unless cancelled\n\nExample: Upgrade on May 15th → Charged May 15th, June 15th, July 15th, etc.'
          }
        ]
      },
      {
        heading: 'Managing Subscription',
        items: [
          {
            q: 'How do I cancel my subscription?',
            a: '1. Go to Profile → Subscription\n2. Click "Manage Subscription"\n3. Click "Cancel Subscription"\n4. Confirm cancellation\n\nYou keep Growth plan access until your billing period ends, then automatically move to Reflect (free) plan. No penalties or fees!'
          },
          {
            q: 'Can I pause my subscription?',
            a: 'We don\'t currently offer subscription pausing. You can cancel anytime and resubscribe later. Your account data is always saved, so you never lose progress!'
          },
          {
            q: 'How do I update my payment method?',
            a: 'Go to Profile → Subscription → Manage Subscription → Update Payment Method. This opens Stripe\'s secure portal where you can update your card details.'
          }
        ]
      },
      {
        heading: 'Refunds & Issues',
        items: [
          {
            q: 'Can I get a refund?',
            a: 'We offer refunds on a case-by-case basis within 7 days of purchase if:\n• Technical issues prevented app use\n• Billing error occurred\n• You were charged incorrectly\n\nContact support@constantloveco.com with your request and reason.'
          },
          {
            q: 'I was charged twice',
            a: 'If you see duplicate charges:\n1. Check if one is a pending authorization (may drop off)\n2. Wait 3-5 business days for pending charges to clear\n3. Contact support@constantloveco.com if still charged twice\n\nWe\'ll resolve billing errors immediately!'
          },
          {
            q: 'I downgraded but still have Growth features',
            a: 'This is normal! When you cancel, you keep Growth plan access until your billing period ends. Check Profile → Subscription to see when your plan changes to Reflect.'
          }
        ]
      }
    ]
  },
  'pwa': {
    title: '📱 Install Constant Love App',
    sections: [
      {
        heading: 'Why Install the App?',
        items: [
          {
            q: 'Why should I install Constant Love?',
            a: 'Benefits of installing:\n• Launch from your home screen like any app\n• Faster loading times\n• Works offline (view saved content)\n• Full-screen experience without browser UI\n• Push notifications for reminders\n• More phone storage-friendly than regular apps\n• Looks and feels like a native app!'
          },
          {
            q: 'Is it free to install?',
            a: 'Yes! Installing Constant Love is completely free. It doesn\'t use extra phone storage (it\'s a Progressive Web App). Your subscription plan stays the same - installation is just a better way to access the app!'
          },
          {
            q: 'Will I lose my data?',
            a: 'No! Installing doesn\'t affect your data at all. Everything stays exactly the same - your journal, Love Vault, Love Score, everything. You\'re just adding a shortcut with enhanced features!'
          }
        ]
      },
      {
        heading: 'How to Install',
        items: [
          {
            q: 'How do I install on iPhone (iOS)?',
            a: 'Step 1: Open Constant Love in Safari browser\nStep 2: Tap the Share button (square with arrow pointing up)\nStep 3: Scroll down and tap "Add to Home Screen"\nStep 4: Name the app (default: "Constant Love")\nStep 5: Tap "Add" in top right\n\nThe app icon appears on your home screen! Tap it to launch.'
          },
          {
            q: 'How do I install on Android?',
            a: 'Step 1: Open Constant Love in Chrome browser\nStep 2: Tap the menu icon (⋮) in top right\nStep 3: Tap "Add to Home screen" or "Install app"\nStep 4: Tap "Install" in the popup\n\nThe app installs and appears on your home screen or app drawer!'
          },
          {
            q: 'How do I install on Desktop?',
            a: 'Chrome/Edge:\n• Look for install icon (⊕) in address bar\n• Click it and select "Install"\n\nSafari (Mac):\n• File menu → Add to Dock\n\nThe app opens in its own window like any desktop application!'
          }
        ]
      },
      {
        heading: 'Using the Installed App',
        items: [
          {
            q: 'How is the installed app different?',
            a: 'Installed app features:\n• Faster loading and performance\n• Full-screen without browser bars\n• Offline access to saved content\n• Push notifications (if enabled)\n• Feels like a native iOS/Android app\n• Uses less data over time\n\nFunctionality remains the same, just better experience!'
          },
          {
            q: 'Can I still use the browser version?',
            a: 'Yes! You can use both:\n• Installed app for daily use\n• Browser version when needed\n\nThey share the same account and data. Use whichever you prefer!'
          },
          {
            q: 'Will I get notifications?',
            a: 'If you enable notifications in app settings, you\'ll get:\n• Daily affirmation reminders\n• Streak reminders\n• Community activity (if opted in)\n• Divine Guidance availability\n\nYou control notification settings in Profile → Settings → Notifications'
          }
        ]
      },
      {
        heading: 'Troubleshooting',
        items: [
          {
            q: 'I don\'t see "Add to Home Screen" option',
            a: 'iOS: Make sure you\'re using Safari (not Chrome or other browsers)\nAndroid: Use Chrome browser for best results\nDesktop: Try Chrome or Edge\n\nSome browsers don\'t support PWA installation. Use Safari (iOS) or Chrome (Android/Desktop).'
          },
          {
            q: 'The installed app won\'t open',
            a: '1. Delete the app icon from home screen\n2. Restart your device\n3. Reinstall following the steps above\n4. Make sure you have internet connection\n5. Contact support if issue persists'
          },
          {
            q: 'How do I uninstall?',
            a: 'iOS: Press and hold the app icon → tap "Remove App" → "Delete App"\n\nAndroid: Press and hold the app icon → drag to "Uninstall" or go to Settings → Apps → Constant Love → Uninstall\n\nDesktop: Right-click app icon → Remove/Uninstall\n\nUninstalling doesn\'t delete your account or data!'
          },
          {
            q: 'Will updates happen automatically?',
            a: 'Yes! The app updates automatically when you open it (requires internet). You don\'t need to download updates from an app store. Always use the latest version effortlessly!'
          }
        ]
      }
    ]
  }
};
